import PropTypes from "prop-types"
import React, { useState } from "react"

import Button from "src/components/Button"
import CollapsibleBar from "src/components/CollapsibleBar"

import AvailabilityDetailsModal from "./AvailabilityDetailsModal"

// Color mapping for each dock type
const TYPE_COLORS = {
  Dockage: {
    dot: "bg-blue-600",
    bar: "bg-blue-600",
  },
  Moorage: {
    dot: "bg-teal-500",
    bar: "bg-teal-500",
  },
  Storage: {
    dot: "bg-supplementary-violet-500",
    bar: "bg-supplementary-violet-500",
  },
  Other: {
    dot: "bg-gray-500",
    bar: "bg-gray-500",
  },
}

// Format number with commas
const formatNumber = (num) => {
  return Math.abs(num).toLocaleString()
}

// Format a metric value with unit
const formatMetricValue = (remaining, unit) => {
  const prefix = remaining < 0 ? "-" : ""
  const formattedNum = formatNumber(remaining)
  return `${prefix}${formattedNum}${unit}`
}

// Get display label for metric type
const getMetricLabel = (metricType) => {
  switch (metricType) {
    case "count":
      return "Count"
    case "linear":
      return "Linear"
    case "sqft":
      return "Sq Ft"
    default:
      return metricType
  }
}

// Get unit suffix for metric type
const getMetricUnit = (metricType) => {
  switch (metricType) {
    case "linear":
      return "'"
    case "sqft":
      return " sqft"
    case "count":
      return " spaces"
    default:
      return ""
  }
}

// Calculate percentage of capacity used
const getUsagePercentage = (remaining, total) => {
  if (total <= 0) return 0
  const used = total - remaining
  return Math.min(Math.max((used / total) * 100, 0), 100)
}

// Summary item for collapsed view
const SummaryItem = ({ type, metrics }) => {
  const colors = TYPE_COLORS[type] || TYPE_COLORS.Other

  const values = metrics
    .map((m) => formatMetricValue(m.remaining, getMetricUnit(m.type)))
    .join(" | ")

  const hasNegative = metrics.some((m) => m.remaining < 0)

  return (
    <div className="flex items-center gap-2">
      <div className={`h-2 w-2 flex-shrink-0 rounded-full ${colors.dot}`} />
      <span className="text-sm text-gray-600">{type}</span>
      <span
        className={`font-mono text-sm font-medium ${
          hasNegative ? "text-red-600" : "text-gray-900"
        }`}
      >
        {values}
      </span>
    </div>
  )
}

SummaryItem.propTypes = {
  type: PropTypes.string.isRequired,
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      remaining: PropTypes.number.isRequired,
      total: PropTypes.number.isRequired,
    })
  ).isRequired,
}

// Metric row for expanded view
const MetricRow = ({ metricType, remaining, total, barColorClass }) => {
  const percentage = getUsagePercentage(remaining, total)
  const isOverbooked = remaining < 0
  const unit = getMetricUnit(metricType)

  return (
    <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:gap-3">
      <span className="w-12 flex-shrink-0 text-xs text-gray-500">
        {getMetricLabel(metricType)}
      </span>

      <div className="order-last h-1.5 w-full overflow-hidden rounded-full bg-gray-200 sm:order-none sm:max-w-[300px] sm:flex-1">
        <div
          className={`duration-400 h-full rounded-full transition-all ${
            isOverbooked ? "bg-red-600" : barColorClass
          }`}
          style={{ width: `${isOverbooked ? 100 : percentage}%` }}
        />
      </div>

      <span
        className={`font-mono flex-shrink-0 text-xs font-medium sm:min-w-[100px] ${
          isOverbooked ? "text-red-600" : "text-gray-900"
        }`}
      >
        {isOverbooked
          ? `${formatNumber(remaining)}${unit} overbooked`
          : `${formatNumber(remaining)}${unit} remaining`}
      </span>

      <span className="hidden text-xs text-gray-500 sm:inline">
        of {formatNumber(total)}
        {metricType !== "count" && unit}
      </span>
    </div>
  )
}

MetricRow.propTypes = {
  metricType: PropTypes.string.isRequired,
  remaining: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  barColorClass: PropTypes.string.isRequired,
}

// Type section for expanded view
const TypeSection = ({ type, metrics }) => {
  const colors = TYPE_COLORS[type] || TYPE_COLORS.Other

  return (
    <div className="flex flex-col gap-2 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex w-full flex-shrink-0 items-center gap-2 leading-5 sm:w-[100px]">
        <div className={`h-2 w-2 flex-shrink-0 rounded-full ${colors.dot}`} />
        <span className="text-sm font-medium text-gray-600">{type}</span>
      </div>

      <div className="flex flex-1 flex-col gap-2">
        {metrics.map((metric) => (
          <MetricRow
            key={metric.type}
            metricType={metric.type}
            remaining={metric.remaining}
            total={metric.total}
            barColorClass={colors.bar}
          />
        ))}
      </div>
    </div>
  )
}

TypeSection.propTypes = {
  type: PropTypes.string.isRequired,
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      remaining: PropTypes.number.isRequired,
      total: PropTypes.number.isRequired,
    })
  ).isRequired,
}

// Main component
const AvailabilityBar = ({
  availabilityData,
  detailedData,
  night,
  onSeeDetails,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const types = Object.keys(availabilityData)

  if (types.length === 0) {
    return null
  }

  const handleSeeDetails = () => {
    if (onSeeDetails) {
      onSeeDetails()
    } else {
      setIsModalOpen(true)
    }
  }

  return (
    <>
      <CollapsibleBar
        title="Availability"
        ariaLabel="Expand availability details"
        summary={
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {types.map((type, index) => (
              <div key={type} className="flex items-center gap-4">
                <SummaryItem type={type} metrics={availabilityData[type]} />
                {index < types.length - 1 && (
                  <span className="text-lg text-gray-300">&bull;</span>
                )}
              </div>
            ))}
          </div>
        }
        actions={
          <Button variant="tertiary" small onClick={handleSeeDetails}>
            <span className="hidden sm:inline">See space groups</span>
            <span className="sm:hidden">Details</span>
          </Button>
        }
      >
        <div className="flex flex-col divide-y divide-gray-200">
          {types.map((type) => (
            <TypeSection
              key={type}
              type={type}
              metrics={availabilityData[type]}
            />
          ))}
        </div>
      </CollapsibleBar>

      <AvailabilityDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        date={night}
        types={detailedData || []}
      />
    </>
  )
}

AvailabilityBar.propTypes = {
  availabilityData: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(["count", "linear", "sqft"]).isRequired,
        remaining: PropTypes.number.isRequired,
        total: PropTypes.number.isRequired,
      })
    )
  ).isRequired,
  detailedData: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      spaceGroups: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          name: PropTypes.string.isRequired,
          total: PropTypes.number.isRequired,
          remaining: PropTypes.number.isRequired,
          unit: PropTypes.oneOf(["count", "linear", "sqft"]).isRequired,
        })
      ).isRequired,
    })
  ),
  night: PropTypes.string,
  onSeeDetails: PropTypes.func,
}

export default AvailabilityBar
